import { ActionMove, ComputedQuery, FiltersMap } from '../model';
import {
  action,
  computed,
  IObservableArray,
  makeObservable,
  reaction,
} from 'mobx';
import { CollectionState } from './CollectionState';
import { Order } from './QueryResultSimulation';
import { CollectionOptimisticActionsState } from './CollectionOptimisticActionsState';
import { CollectionOptimisticProcessor } from './CollectionOptimisticProcessor';
import {
  OptimisticPatch,
  OptimisticPatchParams,
} from './CollectionOptimisticPatch';
import { CollectionOptimisticExecutor } from './CollectionOptimisticExecutor';
import { EventEmitter } from 'events';
import { isHttpError, TypedEmitter } from '../util';
import { QueryResultSimulationDefault } from './QueryResultSimulationDefault';
import { CollectionOptimisticSequenceExecutor } from './CollectionOptimisticSequenceExecutor';
import { ModalLayoutProps } from '../services';
import { QueryState } from './QueryState';
import {
  CreateManyParams,
  DeleteManyParams,
  OptimisticActionBasis,
  OptimisticActions,
  UpdateAllParams,
  UpdateManyParams,
} from './OptimisticActions';
import merge from 'lodash/merge';

export interface MoveParams<T, R> extends OptimisticActionBasis {
  /**
   * Function to submit the move operation.
   * @param params - The parameters for the move action.
   */
  submit?: (params: ActionMove<T>) => Promise<R>;
  /**
   * Snapshot of the collection before the move operation.
   */
  collectionSnapshot: { [key: string]: string | number | undefined };
}

export interface CollectionOptimisticActionsBaseParams<
  T,
  F extends FiltersMap,
> {
  /**
   * A function that simulates the filtering in the server.
   * The function should return another function that accepts an item, and return a boolean that indicates whether the item passes the query filters or not.
   * @param computedQuery - The computed query object.
   * @external
   */
  readonly predicate?: (
    computedQuery: ComputedQuery<F>,
  ) => (item: T) => boolean;
  /**
   * A function that simulates the ordering of the items in the server.<br>
   * The function should return a list of rules in the form of `{fieldName: string; order: 'asc' | 'desc';}` to order the items by
   * @param computedQuery - The computed query object.
   * @external
   */
  readonly orderBy?: (computedQuery: ComputedQuery<F>) => Order<T>[];
  /**
   * A function to retrieve the creation date of an item.
   * @param item - The item.
   * @external
   */
  readonly createdAt?: (item: T) => Date;
  /**
   * Time to wait before batching multiple `updateMany` calls into a single server call.
   * @default 0
   * @external
   */
  readonly batchWait?: number;
  /**
   * Indicates whether to refresh the current page without invalidating the cache.
   * @external
   */
  readonly noInvalidate?: boolean;
  /**
   * Adds a namespace to the cache key.
   * @external
   */
  readonly cacheNamespace?: string;
  readonly sequences?: Map<string, CollectionOptimisticSequenceExecutor>;
  readonly query?: QueryState<F>;
}

export interface CollectionOptimisticActionsParams<T, F extends FiltersMap>
  extends CollectionOptimisticActionsBaseParams<T, F> {
  /**
   * The main collection to reflect changes into.
   * @external
   */
  readonly collection: CollectionState<T, F>;
  /**
   * Additional collections to reflect changes into.
   * @default undefined
   * @external
   */
  readonly collections?: CollectionState<T, F>[];
  /**
   * Container holding patches cache and events emitter.
   * @external
   */
  readonly container: {
    patchesCache: Map<string, IObservableArray<OptimisticPatch<T>>>;
    events: TypedEmitter<{
      navigation: () => ModalLayoutProps | null | undefined;
    }>;
  };
}

export class CollectionOptimisticActions<T, F extends FiltersMap>
  implements OptimisticActions<T>
{
  readonly collection;
  readonly query;
  readonly collections;
  readonly predicate: (computedQuery: ComputedQuery<F>) => (item: T) => boolean;
  readonly orderBy: (computedQuery: ComputedQuery<F>) => Order<T>[];
  readonly createdAt?: (item: T) => Date;
  readonly events = new EventEmitter() as TypedEmitter<{
    reprocess: () => void;
    updated: () => void;
    refresh: (params: { isStale: boolean }) => void;
    unmount: () => void;
    beforeResultUpdated: () => void;
    dragAndDropSequenceError: (params: {
      patches: OptimisticPatch<T>[];
    }) => void;
    dragAndDropSequenceRetry: (params: {
      patches: OptimisticPatch<T>[];
    }) => void;
  }>;
  readonly noInvalidate;
  readonly state;
  readonly processors: CollectionOptimisticProcessor<T, F>[] = [];
  readonly executor: CollectionOptimisticExecutor<OptimisticPatch<T>>;
  readonly sequences;
  readonly resultSimulationDefault;
  readonly reorderModeCanceledMessage = 'REORDER_MODE_CANCELED';

  _resumeNavigation: (() => void) | null = null;

  _orderByMode: 'orders' | 'moves' = 'orders';

  _commitMode = false;

  constructor(params: CollectionOptimisticActionsParams<T, F>) {
    this.collection = params.collection;
    this.query = params.query ?? this.collection.query;
    this.collections = params.collections ?? [];

    this.sequences =
      params.sequences ??
      new Map<string, CollectionOptimisticSequenceExecutor>();

    this.state = new CollectionOptimisticActionsState<T>({
      source: this.collection,
      patchesCache: params.container.patchesCache,
      cacheNamespace: params.cacheNamespace,
    });

    this.resultSimulationDefault = new QueryResultSimulationDefault({
      collection: this.collection,
    });

    this.predicate = params.predicate ?? this.resultSimulationDefault.predicate;
    this.orderBy = params.orderBy ?? this.resultSimulationDefault.orderBy;
    this.createdAt = params.createdAt;
    this.noInvalidate = params.noInvalidate;

    this.executor = new CollectionOptimisticExecutor({
      batchWait: params.batchWait ?? 0,
      errorMonitor: this.collection.errorMonitor,
      showToast: this.collection.showToast,
      translate: this.collection.translate,
      lodash: this.collection.lodash,
      container: params.container,
    });

    this.processors.push(
      new CollectionOptimisticProcessor({
        ...params,
        optimistic: this,
      }),
    );

    if (params.collections != null) {
      params.collections.forEach((collection) => {
        this.processors.push(
          new CollectionOptimisticProcessor({
            ...params,
            collection,
            optimistic: this,
          }),
        );
      });
    }

    this.collection._optimisticActions = this;

    makeObservable(this, {
      pushPatchAndReprocess: action,
      replacePatchAndReprocess: action,
      rollback: action.bound,
      clear: action,
      move: action,
      pendingMovePatches: computed,
    });
  }

  get _queryNames() {
    return [
      this.collection.queryName,
      ...this.collections.map((c) => c.queryName),
    ];
  }

  get hasRunningSequence() {
    return Array.from(this.sequences.values()).some((s) => s.seq.state.running);
  }

  get pendingMovePatches() {
    return this.state.patches.filter((p) => p.move);
  }

  rollback(patch: OptimisticPatch<T> | OptimisticPatch<T>[]) {
    const { events, state } = this;
    const patches = Array.isArray(patch) ? patch : [patch];

    for (const p of patches) {
      state.patches.remove(p);
    }

    events.emit('reprocess');
  }

  private _clearPatch = action((patch: OptimisticPatch<T>) => {
    const {
      state,
      collection,
      collections,
      noInvalidate,
      processors,
      _commitMode,
    } = this;

    if (noInvalidate) {
      return;
    }

    state.patches.remove(patch);
    if (_commitMode && !state.patches.length) {
      processors.forEach(({ processorHelpers }) => processorHelpers.commit());
    }
    collection.invalidate();
    collections.forEach((p) => p.refreshAllPages());
  });

  _clearPatchOnNextResultOrUnmount = (patch: OptimisticPatch<T>) => {
    const { events } = this;

    const clearPatch = () => {
      events.off('unmount', clearPatch);
      this._clearPatch(patch);
    };

    const onRefresh = ({ isStale }: { isStale: boolean }) => {
      events.off('refresh', onRefresh);

      // clear only when results are not going to come from cache
      if (isStale) {
        events.once('beforeResultUpdated', clearPatch);
      }
    };

    events.emit('updated');

    if (this._commitMode) {
      this._clearPatch(patch);
    } else {
      // Clear the patch on the next collection result update to eliminate inconsistencies between server state and local patches
      events.on('refresh', onRefresh);
      // since not clearing the patch immediately, clear it on unmount if was not cleared yet
      events.on('unmount', clearPatch);
    }
  };

  pushPatchAndReprocess(patch: OptimisticPatch<T> | OptimisticPatch<T>[]) {
    const { state, events } = this;
    const patches = Array.isArray(patch) ? patch : [patch];
    state.patches.push(...patches);
    events.emit('reprocess');
  }

  replacePatchAndReprocess(
    newPatch: OptimisticPatch<T>,
    oldPatch: OptimisticPatch<T>,
  ) {
    const { state, events } = this;

    const oldPatchIndex = state.patches.findIndex(
      (patch) => patch === oldPatch,
    );
    state.patches[oldPatchIndex] = newPatch;
    events.emit('reprocess');
  }

  createOne(item: T, params: CreateManyParams<T>) {
    return this.createMany([item], params);
  }

  createMany(items: T[], params: CreateManyParams<T>) {
    const { collection, executor } = this;
    const { itemKey, result } = collection;
    const {
      submit,
      updateServerAfter,
      onUndo,
      onTryAgain,
      onError,
      afterItemKey,
    } = params;

    const optimisticPatchParams: OptimisticPatchParams<T> = {
      queryCache: collection.queryCache,
      queryNames: this._queryNames,
      patches: this.state.patches,
      actionType: 'createMany',
      patchedItems: new Map(items.map((item) => [itemKey(item), item])),
      originalItems: new Map(),
      originalTotal: result.total,
      patch: (item, { patchedItems }) => patchedItems.get(itemKey(item)),
      selector: (item, { patchedItems }) => patchedItems.has(itemKey(item)),
      afterItemKey,
    };

    const optimisticPatch = new OptimisticPatch<T>(optimisticPatchParams);

    this.pushPatchAndReprocess(optimisticPatch);

    optimisticPatch.events.on(
      'scheduleRemoval',
      this._clearPatchOnNextResultOrUnmount,
    );

    return executor.execute([optimisticPatch], {
      ...params,
      submit: action(async (actions) => {
        const submitResult = await submit(
          Array.from(
            actions
              .filter(({ actionType }) => actionType === 'createMany')
              .reduce((map, { patchedItemsEntries }) => {
                for (const [key, item] of patchedItemsEntries) {
                  map.set(key, item);
                }
                return map;
              }, new Map<string, T>())
              .values(),
          ),
        );

        if (!submitResult) {
          return;
        }

        const updatedOptimisticPatch = new OptimisticPatch({
          ...optimisticPatchParams,
          patchedItems: new Map(
            submitResult.map((item) => [itemKey(item), item]),
          ),
        });

        this.replacePatchAndReprocess(updatedOptimisticPatch, optimisticPatch);
        this._clearPatchOnNextResultOrUnmount(updatedOptimisticPatch);
        return submitResult;
      }),
      onRollback: () => {
        this.rollback(optimisticPatch);
      },
      eventuallyUpdated: updateServerAfter,
      onEventuallyUpdated: optimisticPatch.onEventuallyUpdated,
      onTryAgain: async () => {
        this.pushPatchAndReprocess(optimisticPatch);
        await onTryAgain?.();
      },
      onError,
      onUndo,
    });
  }

  /**
   * Updates multiple items in the collection.
   * @param items - The items to update.
   * @param params - Additional parameters for the update operation.
   * @external
   */
  updateMany<R>(items: T[], params: UpdateManyParams<T, R>) {
    const { executor, collection } = this;

    const patchedItems = params.patch
      ? items.map((item) => merge({}, item, params.patch ?? {}))
      : items;

    const { itemKey, result } = collection;
    const {
      submit,
      updateServerAfter,
      keepPosition,
      onUndo,
      onTryAgain,
      onError,
    } = params;

    const originalItems = new Map(
      patchedItems.map((item) => {
        const key = itemKey(item);
        const originalItem = result.get(key)?.item ?? item;
        return [key, originalItem];
      }),
    );

    const optimisticPatch = new OptimisticPatch({
      queryCache: collection.queryCache,
      queryNames: this._queryNames,
      patches: this.state.patches,
      actionType: 'updateMany',
      keepPosition,
      patchedItems: new Map(patchedItems.map((item) => [itemKey(item), item])),
      originalItems,
      originalTotal: result.total,
      patch: (item, { patchedItems }) => patchedItems.get(itemKey(item)),
      selector: (item, { patchedItems }) => patchedItems.has(itemKey(item)),
    });

    this.pushPatchAndReprocess(optimisticPatch);

    optimisticPatch.events.on(
      'scheduleRemoval',
      this._clearPatchOnNextResultOrUnmount,
    );

    return executor.execute([optimisticPatch], {
      ...params,
      submit: action((actions) => {
        const updatedByKey = new Map<string, T>();

        actions
          .filter(({ actionType }) => actionType === 'updateMany')
          .forEach(({ patchedItemsEntries }) => {
            for (const [key, item] of patchedItemsEntries) {
              updatedByKey.set(key, item);
            }
          });

        return submit(Array.from(updatedByKey.values()));
      }),
      onRollback: () => {
        this.rollback(optimisticPatch);
      },
      eventuallyUpdated: updateServerAfter,
      onEventuallyUpdated: optimisticPatch.onEventuallyUpdated,
      onTryAgain: async () => {
        this.pushPatchAndReprocess(optimisticPatch);
        await onTryAgain?.();
      },
      onError,
      onUndo,
    });
  }

  enqueueMovePatch<R>(
    optimisticPatch: OptimisticPatch<T>,
    params: MoveParams<T, R>,
  ) {
    const { submit, onTryAgain } = params;
    const { collection, state, events } = this;
    const { patches } = state;

    const event = optimisticPatch.move;

    if (event == null) {
      return;
    }

    const { filtersKeyHash } = event;

    const _initSequence = () => {
      const { sequences } = this;
      const seq =
        sequences.get(filtersKeyHash) ??
        new CollectionOptimisticSequenceExecutor({
          showToast: collection.showToast,
          translate: collection.translate,
          errorMonitor: collection.errorMonitor,
          onRollback: (err) => {
            const pendingPatches = patches.filter(
              (p) => p.move?.filtersKeyHash === filtersKeyHash,
            );
            this.rollback(pendingPatches);

            if (
              err instanceof Error &&
              err.message === this.reorderModeCanceledMessage
            ) {
              executor.seq.clear();
              return;
            }

            events.emit('dragAndDropSequenceError', {
              patches: pendingPatches,
            });
            executor.seq.clear();

            const requestId = isHttpError(err) ? err.requestId : undefined;

            return {
              message: collection.translate('cairo.dragAndDrop.error.toast'),
              biName: 'cairo-drag-and-drop-submit-error',
              details: requestId
                ? {
                    requestId,
                  }
                : undefined,
              action: {
                onClick: async () => {
                  this.pushPatchAndReprocess(pendingPatches);
                  for (const p of pendingPatches) {
                    this.enqueueMovePatch(p, params);
                  }
                  events.emit('dragAndDropSequenceRetry', {
                    patches: pendingPatches,
                  });

                  await onTryAgain?.();
                },
              },
            };
          },
        });

      if (!sequences.has(filtersKeyHash)) {
        sequences.set(filtersKeyHash, seq);
      }

      return seq;
    };

    const executor = _initSequence();

    executor.sequence({
      submit: () => submit?.(event),
      onEventuallyUpdated: optimisticPatch.onEventuallyUpdated,
    });
  }

  async move<R>(event: ActionMove<T>, params: MoveParams<T, R>) {
    const { collection, state } = this;
    const { itemKey, result } = collection;
    const { patches } = state;

    const optimisticPatch = new OptimisticPatch({
      queryCache: collection.queryCache,
      queryNames: this._queryNames,
      patches,
      actionType: 'move',
      patchedItems: new Map(),
      originalItems: new Map(),
      originalTotal: result.total,
      selector: (item, { patchedItems }) => patchedItems.has(itemKey(item)),
      move: event,
      collectionSnapshot: params.collectionSnapshot,
    });

    this.pushPatchAndReprocess(optimisticPatch);

    optimisticPatch.events.on(
      'scheduleRemoval',
      this._clearPatchOnNextResultOrUnmount,
    );

    this.enqueueMovePatch(optimisticPatch, params);
  }

  /**
   * Updates a single item in the collection.
   * @param item - The item to update.
   * @param params - Additional parameters for the operation.
   * @external
   */
  updateOne<R>(item: T, params: UpdateManyParams<T, R>) {
    return this.updateMany([item], params);
  }

  /**
   * > `updateAll(fn)` API is not recommended as it's not possible to calculate "optimistic total", prefer updateAll(patchObject) API if possible.
   * Updates all items in the collection based on a function or a patch object.
   * @param patchOrFn - A function or a patch object to apply to each item.
   * @param params - Additional parameters for the operation.
   */
  updateAll<R>(
    patchOrFn: (item: T) => Partial<T>,
    params: UpdateAllParams<R>,
  ): void;

  /**
   * Updates all items in the collection based on a patch object.
   * @param patch - A patch object to apply to each item.
   * @param params - Additional parameters for the operation.
   * @external
   */
  updateAll<R>(patch: Partial<T>, params: UpdateAllParams<R>): void;

  updateAll<R>(
    patchOrFn: ((item: T) => Partial<T>) | Partial<T>,
    params: UpdateAllParams<R>,
  ) {
    const { executor, collection, predicate, createdAt } = this;
    const {
      submit,
      updateServerAfter,
      keepPosition,
      onUndo,
      onTryAgain,
      onError,
    } = params;
    const {
      itemKey,
      result: { originQuery, items, total },
    } = collection;

    const itemPredicate = predicate(originQuery);

    const updatedAt = new Date();

    const { patch, actionType } =
      typeof patchOrFn === 'function'
        ? { actionType: 'updateAll' as const, patch: patchOrFn }
        : {
            actionType: 'updateAllConst' as const,
            patch: (item: T) => ({ ...item, ...patchOrFn }),
          };

    const optimisticPatch = new OptimisticPatch({
      queryCache: collection.queryCache,
      patches: this.state.patches,
      actionType,
      keepPosition,
      queryNames: this._queryNames,
      patchedItems: new Map(
        items.map((item) => [itemKey(item), { ...item, ...patch(item) }]),
      ),
      originalItems: new Map(items.map((item) => [itemKey(item), item])),
      originalTotal: total,
      selector: (item) =>
        itemPredicate(item) &&
        (createdAt == null || createdAt(item) <= updatedAt),
      patch,
    });

    this.pushPatchAndReprocess(optimisticPatch);

    optimisticPatch.events.on(
      'scheduleRemoval',
      this._clearPatchOnNextResultOrUnmount,
    );

    executor.execute([optimisticPatch], {
      ...params,
      submit,
      onRollback: () => {
        this.rollback(optimisticPatch);
      },
      eventuallyUpdated: updateServerAfter,
      onEventuallyUpdated: optimisticPatch.onEventuallyUpdated,
      onTryAgain: async () => {
        this.pushPatchAndReprocess(optimisticPatch);
        await onTryAgain?.();
      },
      onError,
      onUndo,
    });
  }

  /**
   * Deletes multiple items from the collection.
   * @param items - An array of items to delete.
   * @param params - Additional parameters for the delete operation.
   * @external
   */
  deleteMany<R>(items: T[], params: DeleteManyParams<T, R>) {
    const { executor, collection } = this;
    const {
      itemKey,
      result: { total },
    } = collection;
    const { submit, updateServerAfter, onUndo, onTryAgain, onError } = params;

    const patchedItems = new Map(items.map((item) => [itemKey(item), item]));

    const optimisticPatch = new OptimisticPatch({
      queryCache: collection.queryCache,
      patches: this.state.patches,
      actionType: 'deleteMany',
      patchedItems,
      queryNames: this._queryNames,
      originalItems: patchedItems,
      originalTotal: total,
      selector: (item, { patchedItems }) => patchedItems.has(itemKey(item)),
    });

    this.pushPatchAndReprocess(optimisticPatch);

    optimisticPatch.events.on(
      'scheduleRemoval',
      this._clearPatchOnNextResultOrUnmount,
    );

    executor.execute([optimisticPatch], {
      ...params,
      submit: action((actions) =>
        submit(
          Array.from(
            actions
              .filter(({ actionType }) => actionType === 'deleteMany')
              .reduce((map, { patchedItemsEntries }) => {
                for (const [key, item] of patchedItemsEntries) {
                  map.set(key, item);
                }
                return map;
              }, new Map<string, T>())
              .values(),
          ),
        ),
      ),
      onRollback: () => {
        this.rollback(optimisticPatch);
      },
      eventuallyUpdated: updateServerAfter,
      onEventuallyUpdated: optimisticPatch.onEventuallyUpdated,
      onTryAgain: async () => {
        this.pushPatchAndReprocess(optimisticPatch);
        await onTryAgain?.();
      },
      onError,
      onUndo,
    });
  }

  /**
   * Deletes a single item from the collection.
   * @param item - The item to delete.
   * @param params - Additional parameters for the operation.
   * @external
   */
  deleteOne<R>(item: T, params: DeleteManyParams<T, R>) {
    return this.deleteMany([item], params);
  }

  /**
   * Deletes all items from the collection.
   * @param params - Additional parameters for the operation.
   * @external
   */
  deleteAll<R>(params: UpdateAllParams<R>) {
    const { executor, collection, predicate, createdAt } = this;
    const { submit, updateServerAfter, onUndo, onTryAgain, onError } = params;
    const {
      itemKey,
      result: { originQuery, items, total },
    } = collection;

    const itemPredicate = predicate(originQuery);

    const updatedAt = new Date();

    const patchedItems = new Map(items.map((item) => [itemKey(item), item]));

    const optimisticPatch = new OptimisticPatch({
      patches: this.state.patches,
      queryCache: collection.queryCache,
      actionType: 'deleteAll',
      patchedItems,
      queryNames: this._queryNames,
      originalItems: patchedItems,
      originalTotal: total,
      selector: (item) =>
        itemPredicate(item) &&
        (createdAt == null || createdAt(item) <= updatedAt),
    });

    optimisticPatch.events.on(
      'scheduleRemoval',
      this._clearPatchOnNextResultOrUnmount,
    );

    this.pushPatchAndReprocess(optimisticPatch);

    executor.execute([optimisticPatch], {
      ...params,
      submit,
      onRollback: () => {
        this.rollback(optimisticPatch);
      },
      eventuallyUpdated: updateServerAfter,
      onEventuallyUpdated: optimisticPatch.onEventuallyUpdated,
      onTryAgain: async () => {
        this.pushPatchAndReprocess(optimisticPatch);
        await onTryAgain?.();
      },
      onError,
      onUndo,
    });
  }

  initCollection(collection: CollectionState<T, F>) {
    this.collections.push(collection);
    new CollectionOptimisticProcessor({
      collection,
      optimistic: this,
      orderBy: this.orderBy,
      predicate: this.predicate,
    });

    return () => {
      const i = this.collections.indexOf(collection);
      if (i !== -1) {
        this.collections.splice(i, 1);
      }
    };
  }

  init() {
    const { collection, events } = this;

    // The only case when this.state._patchesBeforeUnmount won't be null -
    // when the state was cached with patterns router, so we want to restore the patches that were existed before the unmount
    if (this.state._patchesBeforeUnmount) {
      this.state.patches.clear();
      this.state.patches.push(...this.state._patchesBeforeUnmount);
    }

    const emitRefresh = (params: { isStale: boolean }) => {
      events.emit('refresh', params);
    };
    const emitBeforeResultUpdated = () => events.emit('beforeResultUpdated');

    collection.emitter.on('refresh', emitRefresh);
    collection.emitter.on('beforeResultUpdated', emitBeforeResultUpdated);

    const disposers = [
      reaction(
        () => {
          const checkUnprocessedFilteredOut = (() => {
            const {
              result: { _pages, _pagesAtom },
            } = collection;
            _pagesAtom.reportObserved();
            const unprocessed =
              _pages.unprocessed[_pages.unprocessed.length - 1];
            const processed = _pages.processed[_pages.processed.length - 1];

            if (unprocessed == null || processed == null) {
              return false;
            }
            // check if some items in unprocessed were filtered out
            return unprocessed.length > processed.length;
          })();

          const checkIsEmptyAndHasUnprocessed = (() => {
            const {
              result: { isEmpty, _pages },
            } = collection;
            const unprocessed = _pages.unprocessed.flatMap((page) => page);
            return isEmpty && unprocessed.length > 0;
          })();

          return collection.emitter.listeners('triggerReachBottomListener')
            .length
            ? checkUnprocessedFilteredOut
            : checkIsEmptyAndHasUnprocessed;
        },
        (_filteredOut) => {
          if (!_filteredOut) {
            return;
          }

          if (
            collection.emitter.listeners('triggerReachBottomListener').length
          ) {
            if (
              process.env.NODE_ENV === 'test' &&
              (window as any).__CAIRO_TEST_TRIGGER_REACH_BOTTOM_LISTENER__
            ) {
              collection.emitter.emit('triggerReachBottomListener');
            }
          } else {
            collection.fetchNextPageIfNeeded();
          }
        },
      ),
    ];

    return () => {
      collection.emitter.off('refresh', emitRefresh);
      collection.emitter.off('beforeResultUpdated', emitBeforeResultUpdated);
      disposers.forEach((disposer) => disposer());
      this.state._patchesBeforeUnmount = this.state.patches.toJSON();
      events.emit('unmount');
    };
  }

  clear() {
    const { patches } = this.state;
    const remaining = patches.clear();
    for (const patch of remaining) {
      if (!patch.isStale) {
        patches.push(patch);
      }
    }
  }
}
